export const REGISTRATION_ROUTES = {
  EMAIL: "/cadastro-email-musa",
  PHONE: "/cadastro-musa-phone",
  RESERVE_PHONE: "/cadastro-musa-phone-reserva",
  DATA_PASSWORD: "/cadastro-dados-e-senha-musa",
  DOCUMENT: "/cadastro-documento-musa",
  PROFILE_SETUP: "/musa/profile-setup",
};

export const REGISTRATION_FLOW_PATHS = [
  "/cadastro-acompanhante",
  REGISTRATION_ROUTES.EMAIL,
  REGISTRATION_ROUTES.PHONE,
  REGISTRATION_ROUTES.RESERVE_PHONE,
  REGISTRATION_ROUTES.DATA_PASSWORD,
  REGISTRATION_ROUTES.DOCUMENT,
  REGISTRATION_ROUTES.PROFILE_SETUP,
];

export function normalizeRoute(path = "") {
  if (!path) return "/";
  const [clean] = path.split(/[?#]/, 1);
  const collapsed = clean.replace(/\/+/g, "/");
  if (!collapsed) return "/";
  const ensured = collapsed.startsWith("/") ? collapsed : `/${collapsed}`;
  if (ensured === "/") return "/";
  return ensured.replace(/\/$/, "") || "/";
}

const COERCE_TRUE = new Set([true, "true", "1", 1]);
const COERCE_FALSE = new Set([false, "false", "0", 0, ""]);

const normalizeRole = (rawRole) => {
  if (typeof rawRole !== "string") return rawRole;
  const role = rawRole.trim().toLowerCase();
  if (!role) return "";

  if (
    role === "acompanhante" ||
    role.includes("acompanhante") ||
    role === "musa" ||
    role.includes("musa") ||
    role === "escort" ||
    role.includes("escort")
  ) {
    return "acompanhante";
  }

  if (
    role === "cliente" ||
    role.includes("cliente") ||
    role === "client" ||
    role.includes("client")
  ) {
    return "cliente";
  }

  return role;
};

function parseStep(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function coerceBoolean(value) {
  if (COERCE_TRUE.has(value)) return true;
  if (COERCE_FALSE.has(value)) return false;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (COERCE_TRUE.has(normalized)) return true;
    if (COERCE_FALSE.has(normalized)) return false;
  }
  return Boolean(value);
}

export function resolveRegistrationRoute(userLike) {
  if (!userLike) return null;

  const summary = userLike.progress_summary || {};
  const rawRole =
    userLike.role ||
    userLike.user_type ||
    userLike.account_type ||
    userLike.user?.role ||
    summary.role;
  const role = normalizeRole(rawRole);
  if (role !== "acompanhante") return null;

  const progressStep = parseStep(
    summary.current_step ??
    summary.progress_step ??
    userLike.progress_step
  );

  const phoneVerified = summary.phone_verified !== undefined
    ? coerceBoolean(summary.phone_verified)
    : coerceBoolean(userLike.phone_verified);

  // Se já validou o principal mas não concluiu o step de reserva, mantenha na etapa de reserva
  const reserveStepDone = coerceBoolean(
    summary.reserve_phone_step_done ?? userLike.reserve_phone_step_done
  );
  const needsReserveStep = phoneVerified && !reserveStepDone;

  const documentVerified = summary.document_verified !== undefined
    ? coerceBoolean(summary.document_verified)
    : coerceBoolean(userLike.document_verified);
  const documentStatus = (summary.document_status || userLike.document_status || "").toString().toLowerCase();
  const isPendingDocs = documentStatus === "pending_review";
  const needsResubmission = documentStatus === "needs_resubmission";
  const isProgressBeforeDocsComplete = progressStep !== null && progressStep <= 4;

  const profileCompleted = summary.profile_completed !== undefined
    ? coerceBoolean(summary.profile_completed)
    : coerceBoolean(userLike.profile_completed);

  const documentCompleted =
    !needsResubmission &&
    !isProgressBeforeDocsComplete &&
    ((documentVerified || isPendingDocs) || (progressStep !== null && progressStep >= 5));

  if (needsResubmission) {
    // não force rota; deixa o front mostrar CTA/modal de reenvio
    return null;
  }

  if (!documentCompleted) {
    if (needsReserveStep) return REGISTRATION_ROUTES.RESERVE_PHONE;
    // Se telefone principal já estiver verificado, ir para a etapa de reserva; caso contrário, voltar para telefone
    if (progressStep === null) {
      if (!phoneVerified) return REGISTRATION_ROUTES.PHONE;
      return REGISTRATION_ROUTES.DATA_PASSWORD;
    }
    if (progressStep <= 1) return REGISTRATION_ROUTES.EMAIL;
    if (progressStep === 2) return phoneVerified ? REGISTRATION_ROUTES.DATA_PASSWORD : REGISTRATION_ROUTES.PHONE;
    if (progressStep === 3) return REGISTRATION_ROUTES.DATA_PASSWORD;
    return REGISTRATION_ROUTES.DOCUMENT;
  }

  if (!profileCompleted) {
    return REGISTRATION_ROUTES.PROFILE_SETUP;
  }

  return null;
}

export function isInRegistration(userLike) {
  return !!resolveRegistrationRoute(userLike);
}
